import React, { useEffect, useRef, useState } from 'react'
import Constants from '../../../app/utils/constants'
import { motion } from "framer-motion";
import AboutUs3 from '../../../assets/images/aboutuspage/aboutus3.webp'
import AboutUs4 from '../../../assets/images/aboutuspage/aboutus4.webp'
import AboutUsProfile from '../../../assets/images/aboutuspage/himanshusir_image.webp'
const { stats } = Constants

// Small CountUp component using requestAnimationFrame
type CountUpProps = {
  target: number
  duration?: number
  start: boolean
  className?: string
}

const CountUp: React.FC<CountUpProps> = ({ target, duration = 3000, start, className }) => {
  const [value, setValue] = useState(0)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!start) return

    let startTs: number | null = null

    const step = (ts: number) => {
      if (!startTs) startTs = ts
      const elapsed = ts - startTs
      const progress = Math.min(elapsed / duration, 1)
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3)
      const next = Math.floor(target * eased)
      setValue(next)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step)
      } else {
        setValue(target)
      }
    }

    rafRef.current = requestAnimationFrame(step)

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [start, target, duration])

  return (
    <span className={className} aria-live="polite">
      {value}
    </span>
  )
}

type AboutUsStatProps = {
  showStats?: boolean;
  showStoryBlock?: boolean;
  showFounderBlock?: boolean;
};

const AboutUsStat = ({
  showStats = true,
  showStoryBlock = true,
  showFounderBlock = true,
}: AboutUsStatProps) => {
  const gridRef = useRef<HTMLDivElement | null>(null)
  const [started, setStarted] = useState(false)

  useEffect(() => {
    const el = gridRef.current
    if (!el) {
      setStarted(true)
      return
    }

    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true)
      return
    }

    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry && entry.isIntersecting) {
          setStarted(true)
          obs.disconnect()
        }
      },
      { threshold: 0.25 }
    )

    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <section className="w-full pb-5 md:pb-14 lg:pb-20">
      <div className="mx-auto flex w-full flex-col gap-10">

        {showStoryBlock && (
        <div className="relative overflow-hidden my-5">
          <div className="absolute inset-x-0 top-0 z-20 h-[2px] bg-linear-to-r from-[#4530a0] via-[#0f0c22] to-[#47435e]" />
          <div className="absolute inset-x-0 bottom-0 z-20 h-[2px] bg-linear-to-r from-[#1e173d] via-[#4f3ea6] to-[#4530a0]" />
        
         <div className="relative overflow-hidden">
  {/* IMAGE */}
  <img
    src={AboutUs3}
    alt="About us"
    className="h-[480px] w-full object-cover  md:h-[480px] lg:h-[550px]"
  />

  {/* 🔥 TOP GLOW ANIMATION */}
 
<motion.div
  className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[100%] lg:h-[60%] hidden md:block"
  style={{
    background: `
      radial-gradient(circle at center,
        rgba(124,92,255,0.9) 0%,
        rgba(124,92,255,0.6) 20%,
        rgba(124,92,255,0.35) 40%,
        rgba(124,92,255,0.15) 60%,
        transparent 75%
      )
    `,
    filter: "blur(60px)",
  }}
  animate={{
    opacity: [0.3, 1, 0.3],
    scale: [1, 1.15, 1],
  }}
  transition={{
    duration: 2,
    repeat: Infinity,
    ease: "easeIn",
  }}
/>
</div>
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 px-6 text-center">
            <h2 className="font-rajdhani text-2xl font-semibold text-white sm:text-3xl md:text-5xl lg:text-6xl md:mb-5">
            So we built something different.
            </h2>
          <p className="mt-3 max-w-5xl font-gilroy text-sm text-gray-400 sm:text-base md:text-xl lg:text-2xl">
            Today, <span className="font-semibold text-white">The Coding Studio</span> is a{' '}
            <span className="font-semibold text-white">25+ person</span> engineering organization
            specializing in AI automation for regulated industries. We have earned the trust of
            leading healthcare platforms, fintech innovators, and enterprises across India, the
            enterprises globally, and UK. Not through marketing, through delivery.
          </p>
            <p className="mt-3 max-w-5xl font-gilroy text-sm text-gray-400 sm:text-base md:text-xl lg:text-2xl">
            Today, we partner with funded startups, scaling enterprises, and innovative organizations who demand more than code, they demand outcomes. We've built payment systems processing millions, healthcare platforms serving thousands of patients, and AI solutions that have transformed how businesses operate.
            </p>

            
          </div>
          
          
        </div>
        )}

      


        {showStats && (
        <div className="flex justify-center pt-5 sm:pt-6 md:pt-8">
  <div ref={gridRef} className="w-full grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 px-4 sm:px-10 lg:px-20">

    {stats.slice(0, 4).map((item, index) => {
      const hasPlus = item.number.includes('+')
      const numeric = parseInt(item.number.replace('+', ''), 10) || 0

      return (
        <motion.div
          key={`${item.number}-${item.label}`}
          className="rotating-border-gradient mx-auto h-auto w-full max-w-[300px] rounded-[30px] p-px transition-transform duration-300 hover:scale-[1.04]"
          initial={{ opacity: 0, x: -80 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{
            duration: 0.9,
            delay: index * 0.22,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          
          {/* INNER CARD */}
          <div className="relative flex h-auto w-full flex-col items-center justify-center gap-4 overflow-hidden rounded-[30px] bg-[#0b0820] px-4 py-4 md:px-4 md:py-8 text-center transition-all duration-300">
            
            {/* GLOW */}
            <span className="pointer-events-none absolute -top-10 -left-10 h-40 w-40 rounded-full bg-[#6652d4]/30 blur-3xl" />
            <span className="pointer-events-none absolute bottom-0 right-0 h-40 w-40 rounded-full bg-[#745EF0]/10 blur-3xl" />

            {/* NUMBER */}
            <h4 className="font-gilroy text-3xl sm:text-4xl md:text-5xl font-bold text-white">
              <CountUp target={numeric} start={started} duration={2200} className="inline-block" />
              {hasPlus && <span className="text-purple">+</span>}
            </h4>

            {/* LABEL */}
            <p className="w-full h-[50px] md:h-[70px] rounded-[16px] bg-[#14112c] flex items-center justify-center px-4 sm:px-6 font-gilroy text-sm sm:text-base md:text-xl text-white text-center">
              {item.label}
            </p>

          </div>
        </motion.div>
      )
    })}

  </div>
</div>
)}




        {showFounderBlock && (
        <motion.div
          className="relative overflow-hidden"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >

  {/* BACKGROUND IMAGE */}
  <img
    src={AboutUs4}
    alt="Background"
    className="absolute h-full w-full"
  />


  {/* CONTENT */}
  <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-10 xl:px-10 py-7 md:py-10 ">
    
    <div className="flex w-full flex-col items-center gap-10 lg:flex-row 2xl:px-15 2xl:py-16">

    <motion.div
  className="pointer-events-none absolute inset-0 hidden md:block"
  style={{ overflow: 'visible' }}
>
  <motion.div
    className="absolute"
    style={{
      width: 200,
      height: 200,
      borderRadius: '50%',
      background: '#744DFF',
      filter: 'blur(40px)',
      translateX: '-0%',
      translateY: '-100%',
    }}
    animate={{
      top:  ['50%', '100%', '100%'],
      left: ['0%',  '0%',   '20%'],
    }}
    transition={{
      duration: 10,
      ease: 'linear',
      repeat: Infinity,
      repeatType: 'mirror',
    }}
  />
</motion.div>

{/* LEFT — IMAGE SECTION */}
<motion.div
  className="relative flex w-full shrink-0 flex-col items-center lg:w-1/3"
  initial={{ opacity: 0, y: 40 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true, amount: 0.35 }}
  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
>

  {/* PROFILE CONTENT */}
  <div className="relative z-20 flex w-full flex-col text-center items-center">

    {/* PROFILE IMAGE with traveling dark-red glow border */}
    <div className="relative text-center items-center justify-center">

    
      {/* Actual profile image */}
      <img
  src={AboutUsProfile}
  alt="Profile"
  className="relative z-20 mx-auto w-[80%] md:w-[45%] lg:w-full object-contain"
/>
    </div>

    {/* TEXT */}
    <div className="mt-5 flex w-full flex-col items-center text-center">
      <p className="font-gilroy  text-xl md:text-2xl font-bold text-white lg:text-2xl xl:text-3xl">
        Himanshu Goswami
      </p>
      <p className="mt-1 font-gilroy font-semibold text-white text-lg md:text-xl xl:text-2xl">
        Founder & CEO
      </p>
    </div>
  </div>
</motion.div>

      {/* RIGHT — TEXT */}
      <motion.div
        className="flex w-full flex-col gap-5 text-white"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.35 }}
        transition={{ duration: 0.95, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      >
        <h3 className="font-gilroy text-xl md:text-2xl font-[400px] md:text-md lg:text-lg xl:text-xl 2xl:text-3xl">
          <span className="text-purple">Expertise:</span> <span className="text-white">AI/ML Architecture, Automation Systems, Technical Strategy</span>
        </h3>
        <p className=" font-gilroy font-[400px] text-base leading-relaxed text-gray-400 md:text-base xl:text-xl 2xl:text-2xl">
        Himanshu brings over a decade of hands-on experience in AI engineering, automation architecture, and system design. He is not a business - side founder who delegates technical decisions-he is an engineer who built The Coding Studio because he believes regulated industries deserve better technology partners.
        </p>
        <p className=" font-gilroy font-[400px] text-base leading-relaxed text-gray-400 md:text-base xl:text-xl 2xl:text-2xl">
        His background spans the full technical spectrum: from designing ML pipelines that process millions of transactions to architecting compliance frameworks that satisfy the strictest regulators. At The Coding Studio, Himanshu remains directly involved in every enterprise engagement - reviewing architectures, guiding technical decisions, and ensuring delivery meets the standards that built our reputation.
        </p>
        <p className="font-gilroy font-[400px] text-base leading-relaxed text-white md:text-base xl:text-xl 2xl:text-2xl">
          {'"I started TheCoding Studio because I was tired of watching good organizations get burned by vendors who overpromise and underdeliver. In Fintech and Healthcare, that is not just frustrating, it is dangerous. We exist to be the partner these industries can actually trust."'
            .split(' ')
            .map((word, idx) => (
              <motion.span
                key={`${word}-${idx}`}
                className="inline-block mr-1.5"
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.95 }}
                transition={{
                  duration: 0.32,
                  delay: 0.25 + idx * 0.05,
                  ease: 'easeOut',
                }}
              >
                {word}
              </motion.span>
            ))}
        </p>
      </motion.div>

    </div>
  </div>

</motion.div>
)}
      </div>
    </section>
  )
}

export default AboutUsStat
